import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Table, Spin, Alert, Button, Tag, Card, Modal, Form, Input, Select, message, Row, Col, Descriptions, List, Typography, Tabs, Radio, Collapse, Dropdown, Menu, Space, Tooltip, Popconfirm, Divider } from 'antd';
import { ExperimentOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, MedicineBoxOutlined, ExperimentFilled, SolutionOutlined, TeamOutlined, UserSwitchOutlined, AppleOutlined, MoreOutlined, ProfileOutlined, HistoryOutlined, HourglassOutlined, ClockCircleOutlined, ScheduleOutlined, EditOutlined, HeartOutlined, CheckOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import LifestyleOrderCardsModal from './LifestyleOrderCardsModal';
import AddOrderModal from './AddOrderModal';
import PredefinedLabOrdersModal from './PredefinedLabOrdersModal';
import MomentDatePicker from './MomentDatePicker';
import { getLifestyleDisplayLabel } from '../utils/lifestyleDisplayMapping';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const orderTypeIcons = {
  Nursing: <ScheduleOutlined />,
  Medication: <MedicineBoxOutlined />,
  Laboratory: <ExperimentFilled />,
  Radiology: <SolutionOutlined />,
  Referral: <TeamOutlined />,
  Lifestyle: <AppleOutlined />,
  Procedure: <ProfileOutlined />,
  PreventiveScreening: <SafetyCertificateOutlined />,
  Vaccine: <MedicineBoxOutlined />, // Placeholder, consider SafetyCertificateOutlined
  Other: <MoreOutlined />,
};

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
};

// Simple CSS for highlighting - can be moved to a CSS file
const highlightStyle = `
  .highlighted-order-row {
    background-color: #fffbe6 !important; /* Light yellow background */
    transition: background-color 0.5s ease-in-out;
  }
`;

const PatientOrders = ({ patientId, openMedicationModalForOrders, refreshTrigger, onOrderUpdated, highlightOrderId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const orderRowRefs = useRef({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isViewUpdateModalVisible, setIsViewUpdateModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewUpdateLoading, setViewUpdateLoading] = useState(false);
  const [updateForm] = Form.useForm();
  const { user } = useAuth();
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [activeMainTabKey, setActiveMainTabKey] = useState('allOrders');
  const [allOrdersViewModeByStatus, setAllOrdersViewModeByStatus] = useState({
    active: 'chronological',
    pending: 'chronological',
    history: 'chronological',
  });
  const [isLifestyleOrderCardsModalVisible, setIsLifestyleOrderCardsModalVisible] = useState(false);
  const [isPredefinedLabOrdersModalVisible, setIsPredefinedLabOrdersModalVisible] = useState(false);

  const fetchOrders = useCallback(async () => {
     if (!patientId) {
        setLoading(false);
        return;
    }
    setLoading(true);
    setError(null);
      try {
        const response = await axios.get(`/patients/${patientId}/orders`);
        setOrders(response.data.data.orders);
      } catch (err) {
        console.error(`Error fetching orders for patient ${patientId}:`, err);
        setError(err.response?.data?.message || 'Failed to fetch orders.');
      } finally {
        setLoading(false);
      }
  }, [patientId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, refreshTrigger]);

  useEffect(() => {
    if (highlightOrderId && orders.length > 0 && !loading) {
      Object.values(orderRowRefs.current).forEach(ref => {
        if (ref && ref.classList) {
          ref.classList.remove('highlighted-order-row');
        }
      });
      const ref = orderRowRefs.current[highlightOrderId];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.classList.add('highlighted-order-row');
        setTimeout(() => {
          if (ref && ref.classList) {
            ref.classList.remove('highlighted-order-row');
          }
        }, 3000); // Highlight for 3 seconds
      }
    }
  }, [highlightOrderId, orders, loading]);

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const handleAddCancel = () => setIsAddModalVisible(false);
  const handleViewUpdateCancel = () => {
    setIsViewUpdateModalVisible(false);
    setSelectedOrder(null);
    updateForm.resetFields();
  };

  // Handle Lifestyle Order Cards Modal
  const handleLifestyleOrderCardsModal = () => {
    setIsLifestyleOrderCardsModalVisible(true);
  };

  const handleLifestyleOrderCardsClose = () => {
    setIsLifestyleOrderCardsModalVisible(false);
  };

  // Handle Predefined Lab Orders Modal
  const handlePredefinedLabOrdersModal = () => {
    setIsPredefinedLabOrdersModalVisible(true);
  };

  const handlePredefinedLabOrdersClose = () => {
    setIsPredefinedLabOrdersModalVisible(false);
  };

  const handlePredefinedLabOrdersSelected = async (selectedOrders) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    console.log('[PatientOrders] Adding predefined lab orders:', selectedOrders);
    setSubmitLoading(true);

    try {
      // Use bulk order creation API for better performance
      console.log('[PatientOrders] Creating bulk lab orders with payload:', selectedOrders);
      const result = await axios.post(`/patients/${patientId}/orders/bulk`, {
        orders: selectedOrders
      });
      console.log('[PatientOrders] Bulk lab orders created successfully:', result.data);

      message.success({
        content: `Successfully added ${selectedOrders.length} laboratory order(s)! Check the Laboratory tab to view them.`,
        duration: 5
      });

      // Refresh orders and close modal
      await fetchOrders();
      if (onOrderUpdated) onOrderUpdated();
      setIsPredefinedLabOrdersModalVisible(false);

    } catch (err) {
      console.error("Error adding predefined lab orders:", err);
      const errorMsg = err.response?.data?.message || 'Failed to add laboratory orders.';
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleLifestyleOrdersSelected = async (selectedOrders) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    console.log('[PatientOrders] Adding lifestyle orders:', selectedOrders);
    setSubmitLoading(true);

    try {
      // Create orders for each selected lifestyle intervention
      const orderPromises = selectedOrders.map(orderData => {
        const payload = {
          orderType: orderData.orderType,
          orderDetails: orderData.orderDetails,
          status: orderData.status || 'Active',
          patient: patientId,
          orderingProvider: user._id,
          priority: orderData.priority || 'Routine',
          startDate: orderData.startDate,
          endDate: orderData.endDate
        };
        console.log('[PatientOrders] Creating order with payload:', payload);
        return axios.post(`/patients/${patientId}/orders`, payload);
      });

      const results = await Promise.all(orderPromises);
      console.log('[PatientOrders] Orders created successfully:', results.map(r => r.data));

      message.success({
        content: `Successfully added ${selectedOrders.length} lifestyle intervention order(s)! Check the Lifestyle tab to view them.`,
        duration: 5
      });

      // Refresh orders and switch to lifestyle tab to show the new orders
      await fetchOrders();
      if (onOrderUpdated) onOrderUpdated();

      // Optional: Switch to the lifestyle tab to show the new orders
      // This would require passing a callback to change the active tab

    } catch (err) {
      console.error("Error adding lifestyle orders:", err);
      const errorMsg = err.response?.data?.message || 'Failed to add lifestyle orders.';
      message.error(errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const showViewUpdateModal = async (orderId) => {
    setViewUpdateLoading(true); setError(null);
    try {
      const response = await axios.get(`/orders/${orderId}`);
      const orderData = response.data.data.order;
      setSelectedOrder(orderData);
      updateForm.setFieldsValue({
        status: orderData.status, startDate: orderData.startDate ? moment(orderData.startDate) : null, endDate: orderData.endDate ? moment(orderData.endDate) : null,
        resultDate: orderData.results?.resultDate ? moment(orderData.results.resultDate) : null, overallSummary: orderData.results?.overallSummary,
        labComponents: orderData.results?.labComponents || [], findings: orderData.results?.findings, impression: orderData.results?.impression,
      });
      setIsViewUpdateModalVisible(true);
    } catch (err) {
      console.error("Error fetching order details:", err);
      message.error(err.response?.data?.message || 'Failed to fetch order details.');
    } finally {
      setViewUpdateLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      setLoading(true);
      await axios.delete(`/orders/${orderId}`);
      message.success('Order deleted successfully!');
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      message.error('Failed to delete order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (values) => {
     if (!selectedOrder?._id) return;
     setViewUpdateLoading(true);
     try {
        const resultsPayload = {
            resultDate: values.resultDate ? values.resultDate.toISOString() : null, overallSummary: values.overallSummary,
            labComponents: selectedOrder?.orderType === 'Laboratory' ? (values.labComponents || []) : undefined,
            findings: selectedOrder?.orderType === 'Radiology' ? values.findings : undefined, impression: selectedOrder?.orderType === 'Radiology' ? values.impression : undefined,
        };
        // Only remove undefined values, keep null values to allow clearing fields
        Object.keys(resultsPayload).forEach(key => { if (resultsPayload[key] === undefined) delete resultsPayload[key]; });
        const hasResultData = Object.keys(resultsPayload).length > 0;
        // Auto-update status to "Resulted" when Result Date is provided
        let finalStatus = values.status;
        if (values.resultDate && finalStatus !== 'Resulted') {
            finalStatus = 'Resulted';
        }

        const updatePayload = {
            status: finalStatus, startDate: values.startDate ? values.startDate.toISOString() : null, endDate: values.endDate ? values.endDate.toISOString() : null,
            results: hasResultData ? resultsPayload : undefined,
        };

        // If order is Pending, allow editing of order details
        if (selectedOrder.status === 'Pending') {
          let orderDetailsPayload = {};
          switch(selectedOrder.orderType) {
            case 'Laboratory':
              orderDetailsPayload = {
                testName: values.testName,
                specimenType: values.specimenType,
                labName: values.labName
              };
              break;
            case 'Radiology':
              orderDetailsPayload = {
                studyDescription: values.studyDescription,
                reasonForStudy: values.reasonForStudy,
                imagingCenter: values.imagingCenter
              };
              break;
            case 'Referral':
              orderDetailsPayload = {
                referredToProvider: values.referredToProvider,
                specialty: values.specialty,
                reasonForReferral: values.reasonForReferral,
                referralUrgency: values.referralUrgency
              };
              break;
            case 'Lifestyle':
              if (selectedOrder.orderDetails?.lifestyleCategory === 'Nutrition') {
                orderDetailsPayload = {
                  lifestyleCategory: 'Nutrition',
                  baseDiet: values.baseDiet,
                  modifications: values.modifications,
                  lifestyleGeneralInstructions: values.lifestyleGeneralInstructions,
                };
              } else {
                orderDetailsPayload = {
                  lifestyleCategory: selectedOrder.orderDetails?.lifestyleCategory,
                  activityType: values.activityType,
                  frequencyPA: values.frequencyPA,
                  durationPA: values.durationPA,
                  intensityPA: values.intensityPA,
                  specificExercises: values.specificExercises,
                  sleepDurationTarget: values.sleepDurationTarget,
                  sleepHygieneRecommendations: values.sleepHygieneRecommendations,
                  stressReductionTechniques: values.stressReductionTechniques,
                  psychosocialSupportType: values.psychosocialSupportType,
                  psychosocialReferralDetails: values.psychosocialReferralDetails,
                  psychosocialGoals: values.psychosocialGoals,
                  lifestyleGeneralInstructions: values.lifestyleGeneralInstructions,
                };
              }
              Object.keys(orderDetailsPayload).forEach(key => {
                if (orderDetailsPayload[key] === undefined) delete orderDetailsPayload[key];
              });
              break;
            default:
              orderDetailsPayload = {
                description: values.description,
                instructions: values.instructions
              };
              break;
          }
          updatePayload.orderDetails = orderDetailsPayload;
        }
        // Status is already handled above with finalStatus logic
        if (updatePayload.results === undefined) delete updatePayload.results;
        await axios.patch(`/orders/${selectedOrder._id}`, updatePayload);
        
        // Provide user feedback about status auto-update
        if (values.resultDate && values.status !== 'Resulted') {
            message.success('Order updated successfully! Status automatically set to "Resulted" due to Result Date entry.');
        } else {
            message.success('Order updated successfully!');
        }
        
        handleViewUpdateCancel(); fetchOrders();
        if (onOrderUpdated) onOrderUpdated();
     } catch (err) {
        console.error("Error updating order:", err);
        message.error(err.response?.data?.message || 'Failed to update order.');
     } finally {
        setViewUpdateLoading(false);
      }
   };

   const handleQuickStatusUpdate = async ({ key: newStatus }, order) => {
    const orderId = order._id;
    const loadingKey = `status-update-${orderId}`;
    message.loading({ content: 'Updating status...', key: loadingKey, duration: 0 });
    try {
      await axios.patch(`/orders/${orderId}`, { status: newStatus });
      message.success({ content: 'Status updated successfully!', key: loadingKey, duration: 2 });
      fetchOrders();
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      console.error("Error updating order status:", err);
      message.error({ content: err.response?.data?.message || 'Failed to update status.', key: loadingKey, duration: 3 });
    }
   };

   const handleModifyOrder = async (order) => {
    const orderId = order._id;
    const loadingKey = `modify-order-${orderId}`;
    message.loading({ content: 'Modifying order...', key: loadingKey, duration: 0 });
    try {
      await axios.patch(`/orders/${orderId}`, { status: 'Pending' });
      message.success({ content: 'Order status changed to Pending for editing!', key: loadingKey, duration: 2 });
      fetchOrders();
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      console.error("Error modifying order:", err);
      message.error({ content: err.response?.data?.message || 'Failed to modify order.', key: loadingKey, duration: 3 });
    }
   };

   const handleSignAllPendingOrders = async (pendingOrders) => {
    if (!pendingOrders || pendingOrders.length === 0) {
      message.warning('No pending orders to sign.');
      return;
    }

    const loadingKey = 'sign-all-orders';
    message.loading({ content: `Signing ${pendingOrders.length} pending orders...`, key: loadingKey, duration: 0 });

    try {
      const signPromises = pendingOrders.map(order =>
        axios.patch(`/orders/${order._id}`, { status: 'Active' })
      );

      const results = await Promise.allSettled(signPromises);

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (failed === 0) {
        message.success({
          content: `Successfully signed all ${successful} pending orders!`,
          key: loadingKey,
          duration: 3
        });
      } else {
        message.warning({
          content: `Signed ${successful} orders successfully, ${failed} failed.`,
          key: loadingKey,
          duration: 4
        });
      }

      fetchOrders();
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      console.error("Error signing orders:", err);
      message.error({
        content: 'Failed to sign orders. Please try again.',
        key: loadingKey,
        duration: 3
      });
    }
   };

   const getColumnsConfig = (mainTabOrderType = null, statusContext = null) => {
     // Define status filters based on tab context
     let statusFilters = null;
     if (statusContext === 'history') {
       // Only show relevant statuses for Order History tab
       statusFilters = ['Completed', 'Resulted', 'Cancelled'];
     }
     // For 'active' and 'pending' tabs, no status filter needed (statusFilters = null)
     
     const orderStatuses = ['Pending', 'Active', 'Completed', 'Resulted', 'Cancelled'];
     let currentColumns = [
      { title: 'Order Date', dataIndex: 'orderDate', key: 'orderDate', render: (date) => formatDate(date), sorter: (a, b) => new Date(a.orderDate) - new Date(b.orderDate), defaultSortOrder: 'descend'},
      { title: 'Details', key: 'details', render: (_, record) => {
          if (record.orderType === 'Lifestyle') {
              const d = record.orderDetails;
              if (!d) return 'N/A';

              // Check if this is a card-based lifestyle order
              if (d.cardId && d.title) {
                // New card-based lifestyle order
                return d.title;
              }

              // Check for description field (fallback for card-based orders)
              if (d.description && d.description.trim() !== '') {
                return d.description;
              }

              // Legacy lifestyle order handling
              let mainDetail = d.lifestyleGeneralInstructions || '';
              // Show specific order content without redundant category labels
              if (d.lifestyleCategory === 'Nutrition') {
                mainDetail = d.dietaryRecommendations || d.calorieTarget || d.specificFoodsToInclude || mainDetail;
              } else if (d.lifestyleCategory === 'Physical Activity') {
                mainDetail = d.specificExercises ||
                           `${d.activityType || 'Exercise'} - ${d.frequencyPA || ''} ${d.durationPA || ''}`.trim() ||
                           mainDetail;
              } else if (d.lifestyleCategory === 'Rest') {
                mainDetail = d.sleepHygieneRecommendations || d.stressReductionTechniques ||
                           (d.sleepDurationTarget ? `Sleep target: ${d.sleepDurationTarget}` : '') || mainDetail;
              } else if (d.lifestyleCategory === 'PsychosocialManagement') {
                mainDetail = d.psychosocialGoals || d.psychosocialReferralDetails || mainDetail;
              }
              // Return just the content without category prefix since it's already clear from context
              return mainDetail || 'N/A';
          }
          switch(record.orderType) {
              case 'Laboratory': return record.orderDetails?.testName || 'N/A';
              case 'Radiology': return record.orderDetails?.studyDescription || 'N/A';
              case 'Referral': 
                const provider = record.orderDetails?.referredToProvider;
                const specialty = record.orderDetails?.specialty;
                if (provider && specialty) return `To: ${provider} (${specialty})`;
                if (provider) return `To: ${provider}`;
                if (specialty) return `To: ${specialty}`;
                return 'N/A';
              case 'Medication': return record.orderDetails?.medicationName || record.orderDetails?.description || 'N/A';
              default: return record.orderDetails?.description || 'N/A';
          }
      }},
      ...(!mainTabOrderType || mainTabOrderType === 'allOrders' ? [{
        title: 'Type', dataIndex: 'orderType', key: 'orderType', render: (orderType) => (<Tooltip title={orderType}>{orderTypeIcons[orderType] || <MoreOutlined />}</Tooltip>),
        filters: Object.keys(orderTypeIcons).map(type => ({text: type, value: type})), onFilter: (value, record) => record.orderType === value, width: 70, align: 'center',
      }] : []),
      { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', render: (date) => formatDate(date), sorter: (a, b) => new Date(a.startDate) - new Date(b.startDate) },
      { title: 'End Date', dataIndex: 'endDate', key: 'endDate', render: (date) => formatDate(date), sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate) },
      { title: 'Ordering Provider', dataIndex: 'orderingProvider', key: 'orderingProvider', render: (provider) => {
        if (!provider) return 'N/A';
        return provider.name || provider.firstName || 'Unknown';
      }},
      { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => { let color = 'default'; if (status === 'Completed' || status === 'Resulted') color = 'success'; else if (status === 'Pending' || status === 'Active') color = 'processing'; else if (status === 'Cancelled') color = 'error'; return <Tag color={color}>{status?.toUpperCase()}</Tag>; }, ...(statusFilters ? { filters: statusFilters.map(s => ({ text: s, value: s })), onFilter: (value, record) => record.status === value } : {}) },
      { title: 'Actions', key: 'actions', render: (_, record) => {
          let availableStatuses = ['Pending', 'Active', 'Completed', 'Resulted', 'Cancelled'];
          if (record.orderType === 'Medication') availableStatuses = availableStatuses.filter(s => s !== 'Resulted');
          const statusMenu = (<Menu>{availableStatuses.map(status => (<Menu.Item key={status} disabled={record.status === status}><Popconfirm title={`Are you sure to change status to "${status}"?`} onConfirm={() => handleQuickStatusUpdate({ key: status }, record)} okText="Yes" cancelText="No" disabled={record.status === status}><span style={{ display: 'block', width: '100%' }}>{status}</span></Popconfirm></Menu.Item>))}</Menu>);
          return (
            <Space size="small">
              <Tooltip title="View/Update">
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showViewUpdateModal(record._id)} />
              </Tooltip>
              {record.status === 'Active' && (
                <Tooltip title="Modify (Change to Pending for editing)">
                  <Popconfirm
                    title="Change order status to Pending for editing?"
                    onConfirm={() => handleModifyOrder(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" size="small" icon={<EditOutlined />} style={{ color: '#1890ff' }} />
                  </Popconfirm>
                </Tooltip>
              )}
              {record.status !== 'Cancelled' && record.status !== 'Active' && (
                <Dropdown overlay={statusMenu} trigger={['click']}>
                  <Button type="link" size="small" icon={<EditOutlined />} />
                </Dropdown>
              )}
              {(record.status === 'Pending' || record.status === 'Draft') && (
                <Tooltip title="Delete Order">
                  <Popconfirm
                    title="Are you sure you want to delete this order?"
                    onConfirm={() => handleDeleteOrder(record._id)}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okType="danger"
                  >
                    <Button type="link" size="small" icon={<DeleteOutlined />} style={{ color: '#ff4d4f' }} />
                  </Popconfirm>
                </Tooltip>
              )}
            </Space>
          );
         }, fixed: 'right'},
     ];
     return currentColumns.filter(Boolean);
  };

  const getInitialFormValues = () => {
    if (!selectedOrder) return {};

    const baseValues = {
      status: selectedOrder.status,
      startDate: selectedOrder.startDate ? moment(selectedOrder.startDate) : null,
      endDate: selectedOrder.endDate ? moment(selectedOrder.endDate) : null,
      resultDate: selectedOrder.results?.resultDate ? moment(selectedOrder.results.resultDate) : null,
      overallSummary: selectedOrder.results?.overallSummary,
      labComponents: selectedOrder.results?.labComponents || [],
      findings: selectedOrder.results?.findings,
      impression: selectedOrder.results?.impression
    };

    // Add order details for Pending orders
    if (selectedOrder.status === 'Pending' && selectedOrder.orderDetails) {
      const orderDetails = selectedOrder.orderDetails;
      switch(selectedOrder.orderType) {
        case 'Laboratory':
          baseValues.testName = orderDetails.testName;
          baseValues.specimenType = orderDetails.specimenType;
          baseValues.labName = orderDetails.labName;
          break;
        case 'Radiology':
          baseValues.studyDescription = orderDetails.studyDescription;
          baseValues.reasonForStudy = orderDetails.reasonForStudy;
          baseValues.imagingCenter = orderDetails.imagingCenter;
          break;
        case 'Referral':
          baseValues.referredToProvider = orderDetails.referredToProvider;
          baseValues.specialty = orderDetails.specialty;
          baseValues.reasonForReferral = orderDetails.reasonForReferral;
          baseValues.referralUrgency = orderDetails.referralUrgency;
          break;
        case 'Lifestyle':
          baseValues.lifestyleGeneralInstructions = orderDetails.lifestyleGeneralInstructions;
          if (orderDetails.lifestyleCategory === 'PhysicalActivity') {
            baseValues.activityType = orderDetails.activityType;
            baseValues.frequencyPA = orderDetails.frequencyPA;
            baseValues.durationPA = orderDetails.durationPA;
            baseValues.intensityPA = orderDetails.intensityPA;
            baseValues.specificExercises = orderDetails.specificExercises;
          } else if (orderDetails.lifestyleCategory === 'Rest') {
            baseValues.sleepDurationTarget = orderDetails.sleepDurationTarget;
            baseValues.sleepHygieneRecommendations = orderDetails.sleepHygieneRecommendations;
            baseValues.stressReductionTechniques = orderDetails.stressReductionTechniques;
          } else if (orderDetails.lifestyleCategory === 'PsychosocialManagement') {
            baseValues.psychosocialSupportType = orderDetails.psychosocialSupportType;
            baseValues.psychosocialReferralDetails = orderDetails.psychosocialReferralDetails;
            baseValues.psychosocialGoals = orderDetails.psychosocialGoals;
          }
          break;
        default:
          baseValues.description = orderDetails.description;
          baseValues.instructions = orderDetails.instructions;
          break;
      }
    }

    return baseValues;
  };

  const renderEditableOrderDetails = () => {
    if (!selectedOrder || selectedOrder.status !== 'Pending') return null;

    switch(selectedOrder.orderType) {
      case 'Laboratory':
        return (
          <>
            <Divider>Order Details (Editable)</Divider>
            <Form.Item name="testName" label="Test Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="specimenType" label="Specimen Type">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="labName" label="Lab Name">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </>
        );
      case 'Radiology':
        return (
          <>
            <Divider>Order Details (Editable)</Divider>
            <Form.Item name="studyDescription" label="Study Description" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="reasonForStudy" label="Reason for Study">
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item name="imagingCenter" label="Imaging Center">
              <Input />
            </Form.Item>
          </>
        );
      case 'Referral':
        return (
          <>
            <Divider>Order Details (Editable)</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="referredToProvider" label="Referred To Provider/Service" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="specialty" label="Specialty">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="reasonForReferral" label="Reason for Referral" rules={[{ required: true }]}>
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item name="referralUrgency" label="Urgency">
              <Select>
                <Option value="Routine">Routine</Option>
                <Option value="Urgent">Urgent</Option>
                <Option value="STAT">STAT</Option>
              </Select>
            </Form.Item>
          </>
        );
      case 'Lifestyle':
        if (selectedOrder.orderDetails?.lifestyleCategory === 'Nutrition') {
          return (
            <>
              <Divider>Order Details (Editable)</Divider>
              <Form.Item name="lifestyleGeneralInstructions" label="General Lifestyle Instructions">
                <TextArea rows={2} />
              </Form.Item>
            </>
          );
        } else if (selectedOrder.orderDetails?.lifestyleCategory === 'PhysicalActivity') {
          return (
            <>
              <Divider>Order Details (Editable)</Divider>
              <Form.Item name="activityType" label="Activity Type (e.g., Aerobic, Strength)">
                <Input />
              </Form.Item>
              <Form.Item name="frequencyPA" label="Frequency (e.g., 5 times/week)">
                <Input />
              </Form.Item>
              <Form.Item name="durationPA" label="Duration (e.g., 30 mins/session)">
                <Input />
              </Form.Item>
              <Form.Item name="intensityPA" label="Intensity (e.g., Moderate)">
                <Input />
              </Form.Item>
              <Form.Item name="specificExercises" label="Specific Exercises">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="lifestyleGeneralInstructions" label="General Lifestyle Instructions">
                <TextArea rows={2} />
              </Form.Item>
            </>
          );
        } else if (selectedOrder.orderDetails?.lifestyleCategory === 'Rest') {
          return (
            <>
              <Divider>Order Details (Editable)</Divider>
              <Form.Item name="sleepDurationTarget" label="Sleep Duration Target (e.g., 7-8 hours)">
                <Input />
              </Form.Item>
              <Form.Item name="sleepHygieneRecommendations" label="Sleep Hygiene Recommendations">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="stressReductionTechniques" label="Stress Reduction Techniques">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="lifestyleGeneralInstructions" label="General Lifestyle Instructions">
                <TextArea rows={2} />
              </Form.Item>
            </>
          );
        } else if (selectedOrder.orderDetails?.lifestyleCategory === 'PsychosocialManagement') {
          return (
            <>
              <Divider>Order Details (Editable)</Divider>
              <Form.Item name="psychosocialSupportType" label="Support Type (e.g., Counseling, Group)">
                <Input />
              </Form.Item>
              <Form.Item name="psychosocialReferralDetails" label="Referral Details (Contact, etc.)">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="psychosocialGoals" label="Psychosocial Goals">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="lifestyleGeneralInstructions" label="General Lifestyle Instructions">
                <TextArea rows={2} />
              </Form.Item>
            </>
          );
        }
        return null;
      default:
        return (
          <>
            <Divider>Order Details (Editable)</Divider>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="instructions" label="Instructions">
              <TextArea rows={2} />
            </Form.Item>
          </>
        );
    }
  };

  const renderOrderDetailsDescriptions = () => {
    if (!selectedOrder) return null;
    const items = [
        { key: '1', label: 'Order Date', children: new Date(selectedOrder.orderDate).toLocaleString() },
        { key: 'sDate', label: 'Start Date', children: selectedOrder.startDate ? formatDate(selectedOrder.startDate) : 'N/A' },
        { key: 'eDate', label: 'End Date', children: selectedOrder.endDate ? formatDate(selectedOrder.endDate) : 'N/A' },
        { key: '2', label: 'Order Type', children: selectedOrder.orderType },
        { key: '3', label: 'Ordering Provider', children: (() => {
          const provider = selectedOrder.orderingProvider;
          if (!provider) return 'N/A';
          return provider.name || provider.firstName || 'Unknown';
        })() },
        { key: '4', label: 'Status', children: <Tag>{selectedOrder.status}</Tag> },
    ];
    switch(selectedOrder.orderType) {
      case 'Laboratory': items.push({ key: 'd1', label: 'Test Name', children: selectedOrder.orderDetails?.testName || 'N/A' }, { key: 'd2', label: 'Specimen Type', children: selectedOrder.orderDetails?.specimenType || 'N/A' }, { key: 'd3', label: 'Lab Name', children: selectedOrder.orderDetails?.labName || 'N/A' }); break;
      case 'Radiology': items.push({ key: 'd1', label: 'Study Description', children: selectedOrder.orderDetails?.studyDescription || 'N/A' }, { key: 'd2', label: 'Reason', children: selectedOrder.orderDetails?.reasonForStudy || 'N/A' }, { key: 'd3', label: 'Imaging Center', children: selectedOrder.orderDetails?.imagingCenter || 'N/A' }); break;
      case 'Referral': items.push({ key: 'd1', label: 'Referred To', children: selectedOrder.orderDetails?.referredToProvider || 'N/A' }, { key: 'd2', label: 'Specialty', children: selectedOrder.orderDetails?.specialty || 'N/A' }, { key: 'd3', label: 'Reason', children: selectedOrder.orderDetails?.reasonForReferral || 'N/A' }, { key: 'd4', label: 'Urgency', children: selectedOrder.orderDetails?.referralUrgency || 'N/A' }); break;
      case 'Lifestyle':
        // Check if this is a card-based order (new system)
        // Check for cardId OR title (in case cardId is missing but other card data exists)
        if (selectedOrder.orderDetails?.cardId || (selectedOrder.orderDetails?.title && selectedOrder.orderDetails?.smartGoals)) {
          // Basic Information
          items.push(
            { key: 'card_title', label: 'Intervention', children: selectedOrder.orderDetails?.title || 'N/A' },
            { key: 'card_category', label: 'Category', children: selectedOrder.orderDetails?.category || 'N/A' },
            { key: 'card_subcategory', label: 'Subcategory', children: selectedOrder.orderDetails?.subcategory || 'N/A' },
            { key: 'card_duration', label: 'Duration', children: selectedOrder.orderDetails?.duration || 'N/A' }
          );

          // Difficulty and Time Commitment
          if (selectedOrder.orderDetails?.difficulty || selectedOrder.orderDetails?.timeCommitment) {
            let difficultyTimeContent = [];

            if (selectedOrder.orderDetails?.difficulty) {
              difficultyTimeContent.push(
                <Tag key="difficulty" color={selectedOrder.orderDetails.difficulty.color || 'blue'} style={{ marginRight: '8px' }}>
                  {selectedOrder.orderDetails.difficulty.icon} {selectedOrder.orderDetails.difficulty.level}
                </Tag>
              );
            }

            if (selectedOrder.orderDetails?.timeCommitment) {
              difficultyTimeContent.push(
                <Tag key="time" color={selectedOrder.orderDetails.timeCommitment.color || 'orange'}>
                  ⏱️ {selectedOrder.orderDetails.timeCommitment.label}
                </Tag>
              );
            }

            items.push({
              key: 'difficulty_time',
              label: 'Difficulty & Time',
              children: <div>{difficultyTimeContent}</div>
            });
          }

          // Description
          if (selectedOrder.orderDetails?.description) {
            items.push({
              key: 'card_description',
              label: 'Description',
              children: (
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                  {selectedOrder.orderDetails.description}
                </div>
              )
            });
          }

          // Clinical Rationale
          if (selectedOrder.orderDetails?.clinicalRationale) {
            items.push({
              key: 'card_rationale',
              label: 'Clinical Rationale',
              children: (
                <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#1890ff' }}>
                  {selectedOrder.orderDetails.clinicalRationale}
                </div>
              )
            });
          }

          // Complete SMART Goals
          if (selectedOrder.orderDetails?.smartGoals?.length > 0) {
            items.push({
              key: 'smart_goals',
              label: 'Complete SMART Goals',
              children: (
                <List
                  size="small"
                  dataSource={selectedOrder.orderDetails.smartGoals}
                  renderItem={(goal, index) => (
                    <List.Item style={{ padding: '4px 0', fontSize: '13px', borderBottom: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <span style={{
                          backgroundColor: '#52c41a',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          marginRight: '8px',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </span>
                        <span style={{ lineHeight: '1.4' }}>{goal}</span>
                      </div>
                    </List.Item>
                  )}
                />
              )
            });
          }

          // Target Parameters
          if (selectedOrder.orderDetails?.targetParameters?.length > 0) {
            items.push({
              key: 'target_parameters',
              label: 'Target Parameters',
              children: (
                <div style={{ fontSize: '12px' }}>
                  {selectedOrder.orderDetails.targetParameters.map((param, index) => {
                    const displayName = getLifestyleDisplayLabel(param);
                    return (
                      <Tooltip key={index} title={`Standard parameter: ${param}`} placement="top">
                        <Tag style={{ margin: '2px', fontSize: '11px', cursor: 'help' }}>
                          {displayName}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              )
            });
          }

          // Monitoring Parameters
          if (selectedOrder.orderDetails?.monitoring?.length > 0) {
            items.push({
              key: 'monitoring',
              label: 'Monitoring Parameters',
              children: (
                <List
                  size="small"
                  dataSource={selectedOrder.orderDetails.monitoring}
                  renderItem={(item, index) => (
                    <List.Item style={{ padding: '2px 0', fontSize: '12px', borderBottom: 'none' }}>
                      <span style={{ color: '#fa8c16' }}>• </span>{item}
                    </List.Item>
                  )}
                />
              )
            });
          }

          // Contraindications
          if (selectedOrder.orderDetails?.contraindications?.length > 0) {
            items.push({
              key: 'contraindications',
              label: 'Contraindications',
              children: (
                <List
                  size="small"
                  dataSource={selectedOrder.orderDetails.contraindications}
                  renderItem={(item) => (
                    <List.Item style={{ padding: '2px 0', fontSize: '12px', borderBottom: 'none' }}>
                      <span style={{ color: '#ff4d4f' }}>⚠️ </span>
                      <span style={{ color: '#ff4d4f' }}>{item}</span>
                    </List.Item>
                  )}
                />
              )
            });
          }

          // Expected Outcomes
          if (selectedOrder.orderDetails?.expectedOutcomes) {
            items.push({
              key: 'expected_outcomes',
              label: 'Expected Outcomes',
              children: (
                <div style={{
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#52c41a',
                  backgroundColor: '#f6ffed',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #b7eb8f'
                }}>
                  {selectedOrder.orderDetails.expectedOutcomes}
                </div>
              )
            });
          }

          // Evidence Level and Citations
          if (selectedOrder.orderDetails?.evidenceLevel || selectedOrder.orderDetails?.citations?.length > 0) {
            let evidenceContent = [];

            if (selectedOrder.orderDetails?.evidenceLevel) {
              evidenceContent.push(
                <div key="evidence" style={{ marginBottom: '8px' }}>
                  <Tag color={selectedOrder.orderDetails.evidenceLevel.color || 'blue'}>
                    Level {selectedOrder.orderDetails.evidenceLevel.level}
                  </Tag>
                  <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                    {selectedOrder.orderDetails.evidenceLevel.description}
                  </span>
                </div>
              );
            }

            if (selectedOrder.orderDetails?.citations?.length > 0) {
              evidenceContent.push(
                <div key="citations" style={{ fontSize: '11px', color: '#666' }}>
                  <strong>Citations:</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {selectedOrder.orderDetails.citations.map((citation, index) => (
                      <li key={index} style={{ marginBottom: '2px' }}>{citation}</li>
                    ))}
                  </ul>
                </div>
              );
            }

            items.push({
              key: 'evidence_citations',
              label: 'Evidence Base',
              children: <div>{evidenceContent}</div>
            });
          }
        } else {
          // Legacy lifestyle order - show available information

          // Show description if available
          if (selectedOrder.orderDetails?.description) {
            items.push({
              key: 'legacy_description',
              label: 'Description',
              children: (
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                  {selectedOrder.orderDetails.description}
                </div>
              )
            });
          }

          // Show category if available
          if (selectedOrder.orderDetails?.lifestyleCategory) {
            items.push({ key: 'ls_cat', label: 'Lifestyle Category', children: selectedOrder.orderDetails.lifestyleCategory });
          }

          // Category-specific details
          if(selectedOrder.orderDetails?.lifestyleCategory === 'Nutrition') {
            if (selectedOrder.orderDetails.baseDiet) {
              items.push({ key: 'ls_n_base', label: 'Base Diet', children: selectedOrder.orderDetails.baseDiet.name });
            }
            if (selectedOrder.orderDetails.modifications && selectedOrder.orderDetails.modifications.length > 0) {
              items.push({ key: 'ls_n_mods', label: 'Modifications', children: selectedOrder.orderDetails.modifications.map(m => m.name).join(', ') });
            }
          }
          else if (selectedOrder.orderDetails?.lifestyleCategory === 'PhysicalActivity') {
            items.push(
              { key: 'ls_pa1', label: 'Activity Type', children: selectedOrder.orderDetails?.activityType || 'N/A' },
              { key: 'ls_pa2', label: 'Frequency', children: selectedOrder.orderDetails?.frequencyPA || 'N/A' },
              { key: 'ls_pa3', label: 'Duration', children: selectedOrder.orderDetails?.durationPA || 'N/A' },
              { key: 'ls_pa4', label: 'Intensity', children: selectedOrder.orderDetails?.intensityPA || 'N/A' },
              { key: 'ls_pa5', label: 'Specific Exercises', children: selectedOrder.orderDetails?.specificExercises || 'N/A' }
            );
          }
          else if (selectedOrder.orderDetails?.lifestyleCategory === 'Rest') {
            items.push(
              { key: 'ls_r1', label: 'Sleep Target', children: selectedOrder.orderDetails?.sleepDurationTarget || 'N/A' },
              { key: 'ls_r2', label: 'Sleep Hygiene', children: selectedOrder.orderDetails?.sleepHygieneRecommendations || 'N/A' },
              { key: 'ls_r3', label: 'Stress Reduction', children: selectedOrder.orderDetails?.stressReductionTechniques || 'N/A' }
            );
          }
          else if (selectedOrder.orderDetails?.lifestyleCategory === 'PsychosocialManagement') {
            items.push(
              { key: 'ls_pm1', label: 'Support Type', children: selectedOrder.orderDetails?.psychosocialSupportType || 'N/A' },
              { key: 'ls_pm2', label: 'Referral Details', children: selectedOrder.orderDetails?.psychosocialReferralDetails || 'N/A' },
              { key: 'ls_pm3', label: 'Goals', children: selectedOrder.orderDetails?.psychosocialGoals || 'N/A' }
            );
          }

          // General instructions
          if (selectedOrder.orderDetails?.lifestyleGeneralInstructions) {
            items.push({ key: 'ls_gen', label: 'General Instructions', children: selectedOrder.orderDetails.lifestyleGeneralInstructions });
          }

          // Add notice about enhanced clinical cards
          items.push({
            key: 'upgrade_notice',
            label: 'Enhanced Clinical Information',
            children: (
              <div style={{
                fontSize: '12px',
                color: '#1890ff',
                backgroundColor: '#e6f7ff',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #91d5ff'
              }}>
                💡 <strong>Tip:</strong> For comprehensive clinical information including SMART goals, evidence levels, contraindications, and monitoring parameters, use the new <strong>Lifestyle Order Cards</strong> system when creating future orders.
              </div>
            )
          });
        }
        break;
      default: items.push({ key: 'd1', label: 'Description', children: selectedOrder.orderDetails?.description || 'N/A' }, { key: 'd2', label: 'Instructions', children: selectedOrder.orderDetails?.instructions || 'N/A' }); break;
    }
    if (selectedOrder.results) {
        items.push({ key: 'r1', label: 'Result Date', children: formatDate(selectedOrder.results.resultDate) }, { key: 'r2', label: 'Overall Summary', children: selectedOrder.results.overallSummary || 'N/A' });
        if (selectedOrder.orderType === 'Radiology') { items.push({ key: 'r_f', label: 'Findings', children: selectedOrder.results.findings || 'N/A' }, { key: 'r_i', label: 'Impression', children: selectedOrder.results.impression || 'N/A' });}
        else if (selectedOrder.orderType === 'Laboratory' && selectedOrder.results.labComponents && selectedOrder.results.labComponents.length > 0) { items.push({ key: 'r3', label: 'Lab Components', children: ( <List size="small" dataSource={selectedOrder.results.labComponents} renderItem={comp => (<List.Item>{`${comp.testName}: ${comp.value || ''} ${comp.units || ''} ${comp.flag ? `(${comp.flag})` : ''} ${comp.referenceRange ? `[Ref: ${comp.referenceRange}]` : ''}`}</List.Item>)} /> )}); }
    }
    return <Descriptions bordered column={1} items={items} />;
  };

  const renderStatusSubTabs = (mainTabOrderType = null) => {
    const getFilteredOrdersByStatus = (statusArr) => {
      let filtered = orders.filter(o => statusArr.includes(o.status));
      if (mainTabOrderType && mainTabOrderType !== 'allOrders') {
        // For specific main category tabs, filter by that exact orderType
        filtered = filtered.filter(o => o.orderType === mainTabOrderType);
      }
      return filtered;
    };

    const activeOrders = getFilteredOrdersByStatus(['Active']);
    const pendingOrders = getFilteredOrdersByStatus(['Pending']);
    const historyOrders = getFilteredOrdersByStatus(['Completed', 'Resulted', 'Cancelled']);

    const handleViewModeChange = (e, statusType) => {
      setAllOrdersViewModeByStatus(prev => ({ ...prev, [statusType]: e.target.value }));
    };

    const renderContentForStatus = (statusType, ordersForStatus) => {
      if (mainTabOrderType === 'allOrders') {
        const currentViewMode = allOrdersViewModeByStatus[statusType];
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Radio.Group
                onChange={(e) => handleViewModeChange(e, statusType)}
                value={currentViewMode}
              >
                <Radio.Button value="chronological">Chronological</Radio.Button>
                <Radio.Button value="categorical">By Category</Radio.Button>
              </Radio.Group>
              {statusType === 'pending' && ordersForStatus.length > 0 && (
                <Popconfirm
                  title={`Sign all ${ordersForStatus.length} pending orders?`}
                  description="This will change all pending orders to Active status."
                  onConfirm={() => handleSignAllPendingOrders(ordersForStatus)}
                  okText="Yes, Sign All"
                  cancelText="Cancel"
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    Sign All ({ordersForStatus.length})
                  </Button>
                </Popconfirm>
              )}
            </div>
            {currentViewMode === 'chronological' ? (
              <Table 
                columns={getColumnsConfig(mainTabOrderType, statusType)} 
                dataSource={ordersForStatus} 
                rowKey="_id" 
                pagination={{ pageSize: 10 }}
                onRow={(record) => ({
                  ref: el => (orderRowRefs.current[record._id] = el),
                })}
              />
            ) : (
              <Collapse accordion>
                {Object.entries(
                  ordersForStatus.reduce((acc, order) => {
                    (acc[order.orderType] = acc[order.orderType] || []).push(order);
                    return acc;
                  }, {})
                ).map(([orderType, groupedOrders]) => (
                  <Collapse.Panel header={`${orderType} (${groupedOrders.length})`} key={orderType}>
                    <Table 
                      columns={getColumnsConfig(orderType, statusType)} 
                      dataSource={groupedOrders} 
                      rowKey="_id" 
                      pagination={{ pageSize: 5, simple: true }}
                      onRow={(record) => ({
                        ref: el => (orderRowRefs.current[record._id] = el),
                      })}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            )}
          </>
        );
      }
      // For specific main category tabs (Medication, Lab, etc.)
      return (
        <>
          {statusType === 'pending' && ordersForStatus.length > 0 && (
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Popconfirm
                title={`Sign all ${ordersForStatus.length} pending ${mainTabOrderType} orders?`}
                description="This will change all pending orders to Active status."
                onConfirm={() => handleSignAllPendingOrders(ordersForStatus)}
                okText="Yes, Sign All"
                cancelText="Cancel"
              >
                <Button type="primary" icon={<CheckOutlined />}>
                  Sign All ({ordersForStatus.length})
                </Button>
              </Popconfirm>
            </div>
          )}
          <Table
            columns={getColumnsConfig(mainTabOrderType, statusType)}
            dataSource={ordersForStatus}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            onRow={(record) => ({
              ref: el => (orderRowRefs.current[record._id] = el),
            })}
          />
        </>
      );
    };

    return (
      <Tabs type="card" key={`${mainTabOrderType || 'allOrders'}-statusTabs`}>
        <TabPane tab={<><HourglassOutlined /> Active ({activeOrders.length})</>} key={`${mainTabOrderType || 'allOrders'}-active`}>
          <Spin spinning={loading}>
            {renderContentForStatus('active', activeOrders)}
          </Spin>
        </TabPane>
        <TabPane tab={<><ClockCircleOutlined /> Pending ({pendingOrders.length})</>} key={`${mainTabOrderType || 'allOrders'}-pending`}>
          <Spin spinning={loading}>
            {renderContentForStatus('pending', pendingOrders)}
          </Spin>
        </TabPane>
        <TabPane tab={<><HistoryOutlined /> Order History ({historyOrders.length})</>} key={`${mainTabOrderType || 'allOrders'}-history`}>
          <Spin spinning={loading}>
            {renderContentForStatus('history', historyOrders)}
          </Spin>
        </TabPane>
      </Tabs>
    );
  };


  return (
    <>
      <style>{highlightStyle}</style>
      <Card title="Orders" style={{ marginTop: 24 }}>
        {error && !isAddModalVisible && !isViewUpdateModalVisible && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<ExperimentOutlined />} onClick={showAddModal}>
            Add New Order
          </Button>
          <Button
            type="default"
            icon={<ExperimentFilled />}
            onClick={handlePredefinedLabOrdersModal}
            style={{
              borderColor: '#1890ff',
              color: '#1890ff',
              background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%)'
            }}
          >
            Quick Lab Orders
          </Button>
          <Button
            type="default"
            icon={<HeartOutlined />}
            onClick={handleLifestyleOrderCardsModal}
            style={{
              borderColor: '#52c41a',
              color: '#52c41a',
              background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)'
            }}
          >
            Lifestyle Order Cards
          </Button>
        </Space>

        <AddOrderModal
          visible={isAddModalVisible}
          onClose={handleAddCancel}
          onOrderCreated={() => {
            fetchOrders();
            if (onOrderUpdated) onOrderUpdated();
          }}
          patientId={patientId}
          openMedicationModalForOrders={openMedicationModalForOrders}
          preselectedOrderType={activeMainTabKey !== 'allOrders' ? activeMainTabKey.charAt(0).toUpperCase() + activeMainTabKey.slice(1, -1) : null}
        />

        <Modal title="Order Details" visible={isViewUpdateModalVisible} onCancel={handleViewUpdateCancel} footer={null} destroyOnClose width={700}>
          <Spin spinning={viewUpdateLoading}>
              {selectedOrder ? (<><Form form={updateForm} layout="vertical" onFinish={handleUpdateOrder} style={{marginTop: 0}} initialValues={getInitialFormValues()}>
                  {renderOrderDetailsDescriptions()}
                  {renderEditableOrderDetails()}
                  <Row gutter={16} style={{marginTop:16}}>
                    <Col span={12}>
                      <Form.Item name="startDate" label="Start Date">
                        <MomentDatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="endDate" label="End Date">
                        <MomentDatePicker
                          style={{ width: '100%' }}
                          disabledDate={(current) => {
                            const startDate = updateForm.getFieldValue('startDate');
                            return startDate && current && current < moment(startDate).startOf('day');
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="status" label="Update Status" rules={[{ required: true }]}>
                    <Select placeholder="Select new status" disabled={selectedOrder?.status === 'Cancelled'}>
                      <Option value="Pending">Pending</Option>
                      <Option value="Active">Active</Option>
                      <Option value="Completed">Completed</Option>
                      {selectedOrder && selectedOrder.orderType !== 'Medication' && (
                        <Option value="Resulted">Resulted</Option>
                      )}
                      <Option value="Cancelled">Cancelled</Option>
                    </Select>
                  </Form.Item>
                  {selectedOrder.orderType !== 'Lifestyle' && (
                    <>
                      <Form.Item name="resultDate" label="Result Date"><MomentDatePicker showTime style={{ width: '100%' }} /></Form.Item>
                      <Form.Item name="overallSummary" label="Overall Result Summary/Interpretation"><TextArea rows={3} /></Form.Item>
                    </>
                  )}
                    {selectedOrder.orderType === 'Laboratory' && (<div style={{marginTop: '16px'}}><Typography.Text strong>Lab Components:</Typography.Text><Form.List name="labComponents">{(fields, { add, remove }) => (<>{fields.map(({ key, name, ...restField }) => (<Card size="small" key={key} style={{ marginBottom: 8 }}><Row gutter={8} align="middle"><Col flex="auto"><Row gutter={8}><Col span={8}><Form.Item {...restField} name={[name, 'testName']} noStyle><Input placeholder="Test Name" /></Form.Item></Col><Col span={4}><Form.Item {...restField} name={[name, 'value']} noStyle><Input placeholder="Value" /></Form.Item></Col><Col span={4}><Form.Item {...restField} name={[name, 'units']} noStyle><Input placeholder="Units" /></Form.Item></Col><Col span={6}><Form.Item {...restField} name={[name, 'referenceRange']} noStyle><Input placeholder="Ref. Range" /></Form.Item></Col><Col span={2}><Form.Item {...restField} name={[name, 'flag']} noStyle><Input placeholder="Flag" /></Form.Item></Col></Row></Col><Col flex="none"><DeleteOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }}/></Col></Row></Card>))}<Form.Item><Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Lab Component</Button></Form.Item></>)}</Form.List></div>)}
                    {selectedOrder.orderType === 'Radiology' && (<div style={{marginTop: '16px'}}><Form.Item name="findings" label="Findings"><TextArea rows={4} /></Form.Item><Form.Item name="impression" label="Impression"><TextArea rows={3} /></Form.Item></div>)}
                    <Form.Item><Button type="primary" htmlType="submit" loading={viewUpdateLoading}>Save Changes</Button><Button style={{ marginLeft: 8 }} onClick={handleViewUpdateCancel}>Close</Button></Form.Item></Form></>) : (<p>Loading order details...</p>)}
          </Spin>
        </Modal>

        <Tabs defaultActiveKey="allOrders" style={{ marginTop: 16 }} onChange={(key) => setActiveMainTabKey(key)}>
          <TabPane tab={<><ProfileOutlined /> All Orders</>} key="allOrders">
            {renderStatusSubTabs('allOrders')}
          </TabPane>
          <TabPane tab={<><ScheduleOutlined /> Nursing</>} key="nursing">
            {renderStatusSubTabs('Nursing')}
          </TabPane>
          <TabPane tab={<><MedicineBoxOutlined /> Medications</>} key="medications">
            {renderStatusSubTabs('Medication')}
          </TabPane>
          <TabPane tab={<><ExperimentFilled /> Laboratory</>} key="laboratory">
            {renderStatusSubTabs('Laboratory')}
          </TabPane>
          <TabPane tab={<><SolutionOutlined /> Radiology</>} key="radiology">
            {renderStatusSubTabs('Radiology')}
          </TabPane>
          <TabPane tab={<><TeamOutlined /> Referrals</>} key="referrals">
            {renderStatusSubTabs('Referral')}
          </TabPane>
          <TabPane tab={<><AppleOutlined /> Lifestyle</>} key="lifestyle">
            {renderStatusSubTabs('Lifestyle')}
          </TabPane>
          <TabPane tab={<><ProfileOutlined /> Procedure</>} key="procedure">
            {renderStatusSubTabs('Procedure')}
          </TabPane>
          <TabPane tab={<><SafetyCertificateOutlined /> Screening</>} key="screening">
            {renderStatusSubTabs('Preventive Screening')}
          </TabPane>
          <TabPane tab={<><MedicineBoxOutlined /> Vaccines</>} key="vaccines">
            {renderStatusSubTabs('Vaccine')}
          </TabPane>
        </Tabs>
      </Card>

      {/* Lifestyle Order Cards Modal */}
      <LifestyleOrderCardsModal
        visible={isLifestyleOrderCardsModalVisible}
        onClose={handleLifestyleOrderCardsClose}
        patientId={patientId}
        onOrdersSelected={handleLifestyleOrdersSelected}
      />

      {/* Predefined Lab Orders Modal */}
      <PredefinedLabOrdersModal
        visible={isPredefinedLabOrdersModalVisible}
        onClose={handlePredefinedLabOrdersClose}
        patientId={patientId}
        onOrdersSelected={handlePredefinedLabOrdersSelected}
        title="Select Predefined Laboratory Orders"
      />
    </>
  );
};

export default PatientOrders;
